import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  maintenanceApi,
  UtilOverview,
  UtilDailyPoint,
  StationUtil,
  DeviceUtil,
  ConnectorUtil,
  ServiceHealth,
  HealthMonitor,
} from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import {
  Search, ChevronRight, ChevronLeft, ChevronUp, ChevronDown,
  Zap, IndianRupee, Activity, Clock, Gauge, Server,
} from "lucide-react";
import "./maintenance-utilization.scss";
import "../shared/maintenance-analytics.scss";
import { useNotice } from "../shared/useNotice";

/** health-status → .ma-pill variant */
const healthPill = (s: string): string => {
  if (s === "Healthy") return "ok";
  if (s.startsWith("High")) return "bad";
  if (s.startsWith("Idle") || s === "Low Usage") return "warn";
  return "muted"; // Inactive / Never Used
};
const whenStr = (s: string | null) => (s ? new Date(s).toLocaleDateString("en-IN", { year: "2-digit", month: "short", day: "numeric" }) : "never");

const isoStart = (d: string) => `${d}T00:00:00`;
const isoEnd = (d: string) => `${d}T23:59:59`;
const ymd = (dt: Date) => dt.toISOString().slice(0, 10);

const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(Math.round(v))}`);
const pct = (v: number | null | undefined) => (v == null ? "n/a" : `${v}%`);

const pctClass = (v: number | null | undefined) => {
  if (v == null) return "na";
  if (v < 10) return "low";
  if (v < 30) return "mid";
  return "high";
};

/** Inline bar + value for a percentage metric, scaled to the column's max. n/a when null. */
const MBar: React.FC<{ v: number | null | undefined; max: number; kind?: "occ" | "thr" }> = ({ v, max, kind = "occ" }) => {
  if (v == null) return <span className="maint-util-pct na">n/a</span>;
  const cls = kind === "thr" ? "thr" : pctClass(v);
  const w = max > 0 ? Math.max(4, (v / max) * 100) : 4;
  return (
    <div className="maint-u-mbar">
      <div className="track"><div className={`fill ${cls}`} style={{ width: `${w}%` }} /></div>
      <span className="pv">{v}%</span>
    </div>
  );
};

type ChartMetric = "energy" | "sessions" | "revenue";
const CHART_META: Record<ChartMetric, { label: string; color: string; pick: (d: UtilDailyPoint) => number }> = {
  energy:   { label: "Energy (kWh)", color: "#5554d3", pick: (d) => d.energy_kwh },
  sessions: { label: "Sessions",     color: "#e65100", pick: (d) => d.sessions },
  revenue:  { label: "Revenue (₹)",  color: "#2e7d32", pick: (d) => d.revenue },
};

/** Daily energy/sessions/revenue bars + sessions trend line, with hover tooltip. */
const ActivityChart: React.FC<{ daily: UtilDailyPoint[]; metric: ChartMetric }> = ({ daily, metric }) => {
  const [hi, setHi] = useState<number | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  if (!daily.length) return null;

  const W = 1120, H = 210, padL = 48, padR = 14, padB = 26, padT = 10;
  const series = daily.map(CHART_META[metric].pick);
  const line = daily.map((d) => d.sessions);
  const n = series.length, iw = W - padL - padR, ih = H - padT - padB, bw = iw / n;
  const maxB = Math.max(1, ...series) * 1.12, maxL = Math.max(1, ...line) * 1.3;
  const x = (i: number) => padL + i * bw;
  const by = (v: number) => padT + ih - (v / maxB) * ih;
  const ly = (v: number) => padT + ih - (v / maxL) * ih;
  const linePts = line.map((v, i) => `${(x(i) + bw / 2).toFixed(1)},${ly(v).toFixed(1)}`).join(" ");
  const every = Math.max(1, Math.ceil(n / 7));

  return (
    <div className="maint-u-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
           onMouseLeave={() => setHi(null)} onPointerLeave={() => setHi(null)}>
        {[0, 1, 2, 3, 4].map((t) => {
          const yy = padT + (ih * t) / 4;
          return (
            <g key={t}>
              <line className="grid-l" x1={padL} y1={yy} x2={W - padR} y2={yy} />
              <text className="axis-t" x={padL - 8} y={yy + 3} textAnchor="end">{num(maxB * (1 - t / 4))}</text>
            </g>
          );
        })}
        {series.map((v, i) => (
          <rect key={i} className={`maint-u-bar ${hi === i ? "hot" : ""}`} x={x(i) + bw * 0.18} y={by(v)}
            width={bw * 0.64} height={Math.max(0, padT + ih - by(v))} rx={2.5} fill={CHART_META[metric].color} />
        ))}
        <polyline points={linePts} fill="none" stroke="#888" strokeWidth={1.6} />
        {line.map((v, i) => <circle key={i} cx={x(i) + bw / 2} cy={ly(v)} r={2} fill="#888" />)}
        {series.map((_, i) =>
          i % every === 0 ? (
            <text key={i} className="axis-t" x={x(i) + bw / 2} y={H - 8} textAnchor="middle">
              {new Date(daily[i].day).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </text>
          ) : null
        )}
        {/* Pointer events cover mouse, touch and pen. This listened for
            onMouseMove alone, so on a phone the tooltip -- the only way to read
            an exact value off the chart -- never appeared. onPointerDown is what
            a tap fires; pointermove on touch only fires once already dragging. */}
        {series.map((_, i) => (
          <rect key={i} x={x(i)} y={padT} width={bw} height={ih} fill="transparent"
            onPointerDown={(e) => { setHi(i); setPos({ x: e.clientX, y: e.clientY }); }}
            onPointerMove={(e) => { setHi(i); setPos({ x: e.clientX, y: e.clientY }); }} />
        ))}
      </svg>
      {hi != null && (
        <div className="maint-u-tip" style={{ left: Math.min(pos.x + 14, window.innerWidth - 190), top: pos.y - 10 }}>
          <div className="d">{new Date(daily[hi].day).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
          <div className="r"><span>Energy</span><b style={{ color: "#5554d3" }}>{num(daily[hi].energy_kwh, 1)} kWh</b></div>
          <div className="r"><span>Sessions</span><b style={{ color: "#e65100" }}>{daily[hi].sessions}</b></div>
          <div className="r"><span>Revenue</span><b style={{ color: "#2e7d32" }}>{money(daily[hi].revenue)}</b></div>
        </div>
      )}
    </div>
  );
};

type SortKey = "station_name" | "connectors" | "uptime" | "sessions" | "energy_kwh" | "occupancy_pct" | "throughput_pct" | "revenue";
type PresetId = "7" | "30" | "90" | "qtd" | "";
const PRESETS: { id: PresetId; label: string }[] = [
  { id: "7", label: "7d" }, { id: "30", label: "30d" }, { id: "90", label: "90d" }, { id: "qtd", label: "QTD" },
];

const UtilizationDashboard: React.FC = () => {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 864e5);
  const [start, setStart] = useState(ymd(monthAgo));
  const [end, setEnd] = useState(ymd(today));
  const [preset, setPreset] = useState<PresetId>("30");

  const [overview, setOverview] = useState<UtilOverview | null>(null);
  const [daily, setDaily] = useState<UtilDailyPoint[]>([]);
  const [chartMetric, setChartMetric] = useState<ChartMetric>("energy");
  const [loadingOv, setLoadingOv] = useState(true);
  const [health, setHealth] = useState<ServiceHealth | null>(null);
  const [hm, setHm] = useState<HealthMonitor | null>(null);
  const [hmTab, setHmTab] = useState<"stations" | "devices" | "connectors">("stations");

  const [stations, setStations] = useState<StationUtil[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loadingSt, setLoadingSt] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("occupancy_pct");
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const [openStation, setOpenStation] = useState<string | null>(null);
  const [devices, setDevices] = useState<Record<string, DeviceUtil[] | "loading">>({});
  const [openDevice, setOpenDevice] = useState<number | null>(null);
  const [connectors, setConnectors] = useState<Record<number, ConnectorUtil[] | "loading">>({});
  const [error, setError] = useNotice("error", "");

  const win = useCallback(() => ({ start: isoStart(start), end: isoEnd(end) }), [start, end]);

  const applyPreset = (id: PresetId) => {
    const now = new Date();
    let from: Date;
    if (id === "qtd") from = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    else from = new Date(now.getTime() - (id === "7" ? 7 : id === "90" ? 90 : 30) * 864e5);
    setStart(ymd(from)); setEnd(ymd(now)); setPreset(id); setPage(1);
  };

  const fetchOverview = useCallback(async () => {
    setLoadingOv(true);
    try {
      const res = await maintenanceApi.getUtilizationOverview(win());
      setOverview(res.data.overview);
      setDaily(res.data.daily);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load overview");
    } finally { setLoadingOv(false); }
  }, [win]);

  const fetchStations = useCallback(async () => {
    setLoadingSt(true);
    try {
      const params: Record<string, any> = { ...win(), page, page_size: 20 };
      if (search.trim()) params.search = search.trim();
      const res = await maintenanceApi.getUtilizationStations(params);
      setStations(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setOpenStation(null); setOpenDevice(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load stations");
    } finally { setLoadingSt(false); }
  }, [win, page, search]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchStations(); }, [fetchStations]);
  // Effective uptime over the selected window (device_connector_status_log).
  useEffect(() => {
    maintenanceApi.getServiceHealth(win()).then((r) => setHealth(r.data)).catch(() => {});
  }, [win]);
  // Session-outcome health monitor (Redash Q29/Q30/Q48)
  useEffect(() => {
    maintenanceApi.getHealthMonitor(win()).then((r) => setHm(r.data)).catch(() => {});
  }, [win]);

  const toggleStation = async (s: StationUtil) => {
    if (openStation === s.station_guid) { setOpenStation(null); return; }
    setOpenStation(s.station_guid); setOpenDevice(null);
    if (!devices[s.station_guid]) {
      setDevices((p) => ({ ...p, [s.station_guid]: "loading" }));
      try {
        const res = await maintenanceApi.getUtilizationDevices(s.station_guid, win());
        setDevices((p) => ({ ...p, [s.station_guid]: res.data }));
      } catch { setDevices((p) => ({ ...p, [s.station_guid]: [] })); }
    }
  };

  const toggleDevice = async (d: DeviceUtil) => {
    if (openDevice === d.device_id) { setOpenDevice(null); return; }
    setOpenDevice(d.device_id);
    if (!connectors[d.device_id]) {
      setConnectors((p) => ({ ...p, [d.device_id]: "loading" }));
      try {
        const res = await maintenanceApi.getUtilizationConnectors(d.device_id, win());
        setConnectors((p) => ({ ...p, [d.device_id]: res.data }));
      } catch { setConnectors((p) => ({ ...p, [d.device_id]: [] })); }
    }
  };

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(k === "station_name" ? 1 : -1); }
  };
  // per-station effective uptime over the window, keyed by guid for merge into the station table
  const upByStation = useMemo(() => {
    const m: Record<string, number | null> = {};
    health?.stations.forEach((s) => { m[s.station_guid] = s.effective_healthy_pct; });
    return m;
  }, [health]);

  const sorted = useMemo(() => {
    const arr = [...stations];
    const get = (s: StationUtil): any =>
      sortKey === "uptime" ? (upByStation[s.station_guid] ?? -Infinity) : ((s as any)[sortKey] ?? -Infinity);
    arr.sort((a, b) => {
      const av = get(a), bv = get(b);
      return typeof av === "string" ? sortDir * av.localeCompare(bv) : sortDir * (av - bv);
    });
    return arr;
  }, [stations, sortKey, sortDir, upByStation]);

  const maxOcc = Math.max(0.001, ...stations.map((s) => s.occupancy_pct ?? 0));
  const maxThr = Math.max(0.001, ...stations.map((s) => s.throughput_pct ?? 0));
  const leaders = useMemo(
    () => stations.filter((s) => s.occupancy_pct != null).sort((a, b) => (b.occupancy_pct ?? 0) - (a.occupancy_pct ?? 0)).slice(0, 6),
    [stations]
  );

  // hero derivations from the in-service denominator
  const nominalHrs = overview ? overview.connectors * overview.window.range_hours : 0;
  const fullWinOcc = overview && nominalHrs > 0 ? (overview.active_hours / nominalHrs) * 100 : null;
  const occDelta = overview && overview.occupancy_pct != null && fullWinOcc ? (overview.occupancy_pct / fullWinOcc - 1) * 100 : null;

  // service health: effective uptime over the selected window (device_connector_status_log)
  const sh = health?.summary ?? null;

  const SortTh: React.FC<{ k: SortKey; label: string; numeric?: boolean }> = ({ k, label, numeric }) => (
    <th className={`maint-u-sortable ${numeric ? "num" : ""} ${sortKey === k ? "on" : ""}`} onClick={() => onSort(k)}>
      {label}{sortKey === k && (sortDir < 0 ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
    </th>
  );

  const KPI: React.FC<{ icon: React.ReactNode; bg: string; label: string; value: React.ReactNode; sub: string }> =
    ({ icon, bg, label, value, sub }) => (
      <div className="maint-kpi">
        <div className="maint-u-kpi-ic" style={{ background: bg }}>{icon}</div>
        <div className="maint-kpi-label">{label}</div>
        <div className="maint-kpi-value">{value}</div>
        <div className="maint-kpi-sub">{sub}</div>
      </div>
    );

  return (
    <MaintenanceLayout title="Charger Utilization (Maintenance)">
      <div className="maint-stations">
        {/* toolbar */}
        <div className="maint-u-toolbar">
          <div className="maint-u-presets">
            {PRESETS.map((p) => (
              <button key={p.id} className={`maint-u-preset ${preset === p.id ? "on" : ""}`} onClick={() => applyPreset(p.id)}>{p.label}</button>
            ))}
          </div>
          <label className="maint-u-date">From<input type="date" value={start} max={end}
            onChange={(e) => { setStart(e.target.value); setPreset(""); }} /></label>
          <label className="maint-u-date">To<input type="date" value={end} min={start}
            onChange={(e) => { setEnd(e.target.value); setPreset(""); }} /></label>
          <span style={{ flex: 1 }} />
          <form className="maint-search" style={{ maxWidth: 280 }}
            onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }}>
            <Search size={16} />
            <input type="text" placeholder="Search station or org..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)} />
          </form>
        </div>

        {error && <p className="maint-form-error">{error}</p>}

        <div className="maint-util-note">
          Cross-tenant — <strong>excludes “Test Private”</strong>. Occupancy &amp; throughput count each connector only for the
          hours it was actually in service (commissioning → last seen), not the full window; <strong>n/a</strong> = out of
          service all period. Revenue = paid sessions only.
        </div>

        {loadingOv || !overview ? (
          <p className="maint-loading">Loading overview...</p>
        ) : (
          <>
            {/* hero */}
            <div className="maint-u-hero">
              <div className="maint-u-metric occ">
                <div className="maint-u-metric-label">Occupancy</div>
                <div className="maint-u-metric-value">{pct(overview.occupancy_pct)}</div>
                <div className="maint-u-metric-sub">
                  active hrs ÷ <b>{num(overview.in_service_connector_hours)}</b> in-service hrs
                  {occDelta != null && <span className="maint-u-chip up">▲ {occDelta >= 0 ? "+" : ""}{num(occDelta)}% vs full-window</span>}
                </div>
              </div>
              <div className="maint-u-metric thr">
                <div className="maint-u-metric-label">Throughput</div>
                <div className="maint-u-metric-value">{pct(overview.throughput_pct)}</div>
                <div className="maint-u-metric-sub">
                  energy ÷ rated capacity · <b>{overview.capacity_coverage_pct}%</b> coverage
                </div>
              </div>
            </div>

            {/* KPI strip */}
            <div className="maint-kpis">
              <KPI label="Energy sold" value={num(overview.energy_kwh, 1)} sub="kWh delivered" bg="#e6f9e6" icon={<Zap size={14} color="#2e7d32" />} />
              <KPI label="Revenue" value={money(overview.revenue)} sub="paid sessions" bg="#eef0ff" icon={<IndianRupee size={14} color="#5554d3" />} />
              <KPI label="Sessions" value={num(overview.sessions)} sub={`${num(overview.active_hours)} active hrs`} bg="#fff3e0" icon={<Activity size={14} color="#e65100" />} />
              <KPI label="Active hours" value={num(overview.active_hours)} sub="charging time" bg="#e3f2fd" icon={<Clock size={14} color="#1565c0" />} />
              <KPI label="Avg power" value={overview.avg_power_kw == null ? "—" : overview.avg_power_kw} sub="kW / active hr" bg="#f3e8ff" icon={<Gauge size={14} color="#7b3ff2" />} />
              <KPI label="Fleet" value={num(overview.connectors)} sub={`${overview.devices} dev · ${overview.stations} stn · ${overview.orgs} orgs`} bg="#f0f0f0" icon={<Server size={14} color="#888" />} />
            </div>

            {/* chart */}
            <div className="maint-u-card maint-u-chart-card">
              <div className="maint-u-card-h">
                <h3>Activity over time</h3>
                <div className="maint-u-chart-controls">
                  <span className="maint-u-lg"><i style={{ background: CHART_META[chartMetric].color }} />{CHART_META[chartMetric].label}</span>
                  <span className="maint-u-lg"><i style={{ background: "#888" }} />Sessions (line)</span>
                  <div className="maint-u-toggles">
                    {(["energy", "sessions", "revenue"] as ChartMetric[]).map((m) => (
                      <button key={m} className={`maint-u-tg ${chartMetric === m ? "on" : ""}`} onClick={() => setChartMetric(m)}>
                        {m[0].toUpperCase() + m.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <ActivityChart daily={daily} metric={chartMetric} />
            </div>
          </>
        )}

        {/* lower split: table + side */}
        <div className="maint-u-lower">
          <div className="maint-u-card">
            <div className="maint-u-card-h">
              <h3>Stations {!loadingSt && <span className="maint-u-hint">· {stations.length} of {total}</span>}</h3>
              <span className="maint-u-hint">click a row → devices → connectors · bars scaled to column max</span>
            </div>

            {loadingSt ? (
              <p className="maint-loading">Loading stations...</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="maint-table maint-u-table">
                  <thead>
                    <tr>
                      <SortTh k="station_name" label="Station" />
                      <SortTh k="connectors" label="Conn" numeric />
                      <SortTh k="uptime" label="Uptime" numeric />
                      <SortTh k="sessions" label="Sessions" numeric />
                      <SortTh k="energy_kwh" label="Energy kWh" numeric />
                      <SortTh k="occupancy_pct" label="Occupancy" numeric />
                      <SortTh k="throughput_pct" label="Throughput" numeric />
                      <SortTh k="revenue" label="Revenue" numeric />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s) => {
                      const open = openStation === s.station_guid;
                      const dev = devices[s.station_guid];
                      return (
                        <React.Fragment key={s.station_guid}>
                          <tr className={`maint-util-row ${open ? "open" : ""}`} onClick={() => toggleStation(s)}>
                            <td>
                              <div className="maint-u-stname">
                                <ChevronRight size={14} className="maint-util-caret" />
                                <span className={`maint-u-dot ${s.occupancy_pct == null ? "out" : "active"}`} />
                                <div>
                                  <div className="t">{s.station_name}</div>
                                  <div className="o">{s.org_name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="num">{s.connectors}</td>
                            <td className="num"><span className={`maint-util-pct ${pctClass(upByStation[s.station_guid])}`}>{pct(upByStation[s.station_guid])}</span></td>
                            <td className="num">{num(s.sessions)}</td>
                            <td className="num">{num(s.energy_kwh, 1)}</td>
                            <td className="num"><MBar v={s.occupancy_pct} max={maxOcc} kind="occ" /></td>
                            <td className="num"><MBar v={s.throughput_pct} max={maxThr} kind="thr" /></td>
                            <td className="num">{money(s.revenue)}</td>
                          </tr>
                          {open && (
                            <tr className="maint-util-subrow">
                              <td colSpan={8}>
                                {dev === "loading" || dev === undefined ? (
                                  <p className="maint-loading" style={{ padding: 16 }}>Loading devices...</p>
                                ) : dev.length === 0 ? (
                                  <p className="maint-empty" style={{ padding: 16 }}>No devices</p>
                                ) : (
                                  <DeviceTable
                                    devices={dev}
                                    openDevice={openDevice}
                                    connectors={connectors}
                                    onToggle={toggleDevice}
                                  />
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {stations.length === 0 && <tr><td colSpan={8} className="maint-empty">No stations found</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            <div className="maint-pagination" style={{ padding: "12px 16px" }}>
              <span>{total} stations total</span>
              <div className="maint-pagination-controls">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={16} /></button>
                <span>Page {page} of {totalPages || 1}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={16} /></button>
              </div>
            </div>
          </div>

          {/* side rail */}
          <div className="maint-u-side">
            <div className="maint-u-card">
              <div className="maint-u-card-h"><h3>Top stations by occupancy</h3></div>
              <div className="maint-u-lb">
                {leaders.length === 0 && <p className="maint-empty" style={{ padding: 8 }}>No data</p>}
                {leaders.map((s, i) => (
                  <div className="maint-u-lbrow" key={s.station_guid}>
                    <span className="rk">{i + 1}</span>
                    <div className="nm">
                      <div className="t">{s.station_name}</div>
                      <div className="bar"><i style={{ width: `${((s.occupancy_pct ?? 0) / (leaders[0].occupancy_pct ?? 1)) * 100}%` }} /></div>
                    </div>
                    <span className="pv">{s.occupancy_pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {sh && (
              <div className="maint-u-card maint-u-health">
                <h3>Service health</h3>
                <div className="hbig">{sh.effective_healthy_pct == null ? "—" : `${num(sh.effective_healthy_pct)}%`}<small> effective uptime</small></div>
                {sh.effective_healthy_pct != null && sh.uptime_pct != null ? (
                  <>
                    <div className="hsplit">
                      <i style={{ width: `${Math.min(100, sh.effective_healthy_pct)}%`, background: "#2e7d32" }} />
                      <i style={{ width: `${Math.max(0, sh.uptime_pct - sh.effective_healthy_pct)}%`, background: "#e65100" }} />
                      <i style={{ width: `${Math.max(0, 100 - sh.uptime_pct)}%`, background: "#9aa0ab" }} />
                    </div>
                    <div className="hleg">
                      <span><i style={{ background: "#2e7d32" }} />Healthy &amp; reachable <b>{num(sh.effective_healthy_pct)}%</b></span>
                      <span><i style={{ background: "#e65100" }} />Faulted <b>{num(sh.faulted_hours)}h</b></span>
                      <span><i style={{ background: "#9aa0ab" }} />Offline <b>{num(sh.offline_hours)}h</b></span>
                    </div>
                    <div className="ends">
                      <span>connectivity {num(sh.uptime_pct)}%</span>
                      <span>{sh.stations} stations · {sh.connectors} chargers</span>
                    </div>
                  </>
                ) : (
                  <div className="ends"><span>No devices active in this window</span></div>
                )}
                <div className="hnote">
                  Effective uptime over the selected range from the connector status log — the share of in-service
                  time a charger was reachable &amp; not faulted. Excludes Test Private and devices not seen in the window.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Health monitor — session-outcome health (migrated Redash Q29/Q30/Q48) */}
        <div className="ma" style={{ marginTop: 16 }}>
          <div className="ma-card">
            <div className="ma-card-h">
              <h3>Health monitor <span className="ma-hint">· incomplete / zero-energy / idle · excludes Test Private</span></h3>
              <div className="ma-tabs" style={{ margin: 0, border: "none" }}>
                {(["stations", "devices", "connectors"] as const).map((t) => (
                  <button key={t} className={`ma-tab ${hmTab === t ? "on" : ""}`} onClick={() => setHmTab(t)}>
                    {t === "stations" ? `Stations${hm ? ` (${hm.stations.length})` : ""}`
                      : t === "devices" ? `Devices${hm ? ` (${hm.devices.length})` : ""}`
                        : `Underperforming${hm ? ` (${hm.connectors.length})` : ""}`}
                  </button>
                ))}
              </div>
            </div>
            <div className="ma-tablewrap">
              {!hm ? <p className="ma-loading">Loading health…</p> : hmTab === "stations" ? (
                <table className="ma-table">
                  <thead><tr><th>Station</th><th>Org</th><th className="num">Sessions</th><th className="num">Incomplete</th><th className="num">Zero-kWh</th><th>Last session</th><th>Status</th></tr></thead>
                  <tbody>
                    {hm.stations.map((s) => (
                      <tr key={s.station_guid}>
                        <td>{s.station}</td>
                        <td className="sub">{s.org_name || "—"}</td>
                        <td className="num">{num(s.sessions)}</td>
                        <td className="num">{num(s.incomplete)}</td>
                        <td className="num">{num(s.zero_energy)}</td>
                        <td className="sub">{whenStr(s.last_session)}</td>
                        <td><span className={`ma-pill ${healthPill(s.health_status)}`}>{s.health_status}</span></td>
                      </tr>
                    ))}
                    {!hm.stations.length && <tr><td colSpan={7} className="ma-empty">No stations</td></tr>}
                  </tbody>
                </table>
              ) : hmTab === "devices" ? (
                <table className="ma-table">
                  <thead><tr><th>Device</th><th>Station</th><th>OCPP</th><th className="num">Sessions</th><th className="num">Incomplete</th><th className="num">Zero-kWh</th><th>Last session</th><th>Status</th></tr></thead>
                  <tbody>
                    {hm.devices.map((d) => (
                      <tr key={d.device_id}>
                        <td>{d.device_name || `#${d.device_id}`}</td>
                        <td className="sub">{d.station || "—"}</td>
                        <td className="sub">{d.ocpp_version || "—"}</td>
                        <td className="num">{num(d.sessions)}</td>
                        <td className="num">{num(d.incomplete)}</td>
                        <td className="num">{num(d.zero_energy)}</td>
                        <td className="sub">{whenStr(d.last_session)}</td>
                        <td><span className={`ma-pill ${healthPill(d.health_status)}`}>{d.health_status}</span></td>
                      </tr>
                    ))}
                    {!hm.devices.length && <tr><td colSpan={8} className="ma-empty">No devices</td></tr>}
                  </tbody>
                </table>
              ) : (
                <table className="ma-table">
                  <thead><tr><th>Connector</th><th>Device</th><th>Station</th><th>Current</th><th className="num">Sessions (win)</th><th>Last session</th><th>Flag</th></tr></thead>
                  <tbody>
                    {hm.connectors.map((c) => (
                      <tr key={c.connector_id}>
                        <td>{c.connector_name || "—"}</td>
                        <td className="sub">{c.device_name || "—"}</td>
                        <td className="sub">{c.station || "—"}</td>
                        <td><span className="ma-pill muted">{c.current_status || "—"}</span></td>
                        <td className="num">{num(c.sessions_window)}</td>
                        <td className="sub">{whenStr(c.last_session)}</td>
                        <td><span className={`ma-pill ${healthPill(c.health_flag)}`}>{c.health_flag}</span></td>
                      </tr>
                    ))}
                    {!hm.connectors.length && <tr><td colSpan={7} className="ma-empty">No underperforming connectors</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </MaintenanceLayout>
  );
};

/** Device drill-down table (with its own connector drill-down). */
const DeviceTable: React.FC<{
  devices: DeviceUtil[];
  openDevice: number | null;
  connectors: Record<number, ConnectorUtil[] | "loading">;
  onToggle: (d: DeviceUtil) => void;
}> = ({ devices, openDevice, connectors, onToggle }) => {
  const maxOcc = Math.max(0.001, ...devices.map((d) => d.occupancy_pct ?? 0));
  const maxThr = Math.max(0.001, ...devices.map((d) => d.throughput_pct ?? 0));
  return (
    <table className="maint-util-subtable">
      <thead>
        <tr>
          <th className="pad-l">Device</th>
          <th>Conn</th><th>Sessions</th><th>Energy kWh</th>
          <th>Occupancy</th><th>Throughput</th><th>Revenue</th>
        </tr>
      </thead>
      <tbody>
        {devices.map((d) => {
          const dopen = openDevice === d.device_id;
          const con = connectors[d.device_id];
          const maxCocc = con && con !== "loading" ? Math.max(0.001, ...con.map((c) => c.occupancy_pct ?? 0)) : 1;
          return (
            <React.Fragment key={d.device_id}>
              <tr className={`maint-util-row ${dopen ? "open" : ""}`} onClick={() => onToggle(d)}>
                <td className="pad-l">
                  <ChevronRight size={13} className="maint-util-caret" />{" "}
                  {d.display_name || d.device_name || `#${d.device_id}`}
                </td>
                <td>{d.connectors}</td>
                <td>{num(d.sessions)}</td>
                <td>{num(d.energy_kwh, 1)}</td>
                <td><MBar v={d.occupancy_pct} max={maxOcc} kind="occ" /></td>
                <td><MBar v={d.throughput_pct} max={maxThr} kind="thr" /></td>
                <td>{money(d.revenue)}</td>
              </tr>
              {dopen && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    {con === "loading" || con === undefined ? (
                      <p className="maint-loading" style={{ padding: 12 }}>Loading connectors...</p>
                    ) : con.length === 0 ? (
                      <p className="maint-empty" style={{ padding: 12 }}>No connectors</p>
                    ) : (
                      <table className="maint-util-subtable lvl2">
                        <thead>
                          <tr>
                            <th className="pad-l">Connector</th>
                            <th>Socket</th><th>kW</th><th>Sessions</th>
                            <th>Energy kWh</th><th>Occupancy</th><th>Throughput</th>
                          </tr>
                        </thead>
                        <tbody>
                          {con.map((c) => (
                            <tr key={c.connector_id}>
                              <td className="pad-l">{c.connector_name}</td>
                              <td>{c.socket_name || "—"}</td>
                              <td>{c.capacity_kwh ?? "—"}</td>
                              <td>{num(c.sessions)}</td>
                              <td>{num(c.energy_kwh, 1)}</td>
                              <td><MBar v={c.occupancy_pct} max={maxCocc} kind="occ" /></td>
                              <td>{c.throughput_pct == null ? <span className="maint-util-pct na">n/a</span> : `${c.throughput_pct}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default UtilizationDashboard;
